import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';

import { UseMutationResult } from '@tanstack/react-query';

import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { SortableList } from '@components/drag-and-drop';
import { DraggableComponentProps } from '@components/drag-and-drop/sortable-list';
import { EmptySearchState } from '@components/empty-search-state';
import { EmptyStateCell } from '@components/empty-state';
import { FocusBox } from '@components/focus-box';
import { setContinueRegistry, useDeleteModal } from '@components/generic-modals';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTrash } from '@components/icons/trash';
import { IconVisibilityHidden } from '@components/icons/visibility-hidden';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { Menu } from '@components/menu';
import { PageContent } from '@components/page-containers/page-container';
import { MiddleColumnPage } from '@components/page-containers/page-containers';
import { useIsSidebarActive } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { reorderList } from '@utils/collections';

import { AttributeType } from 'api/attributes/requests';
import {
  useCreateAttribute,
  useCreateJourney,
  useRemoveAttribute,
  useUpdateAttribute,
  useUpdateAttributeOrders,
} from 'api/journeys/hooks';
import { AttributeData, JourneyAttributeData } from 'api/journeys/requests';
import { routePaths } from 'ui/authenticated/navigation';
import { FieldCard } from 'ui/authenticated/settings/pages/attributes/field-card';
import { useFieldFormSidebar } from 'ui/authenticated/settings/pages/attributes/sidebars/field-form';

import { useCurrentWorkspace } from '../../helpers';

const useAddJourneyField = (journeyShortId: string) => {
  const { workspaceId } = useCurrentWorkspace();
  const navigate = useNavigate();
  const createJourneyMutation = useCreateJourney();
  const showAddAttributeSidebar = useFieldFormSidebar();
  const createAttributeMutation = useCreateAttribute();

  const addAttribute = async () => {
    if (journeyShortId === '') {
      const journeyData = await createJourneyMutation.mutateAsync({
        workspace: workspaceId ?? '',
        title: 'Untitled journey',
      });
      navigate(
        generatePath(routePaths.editJourney, {
          workspaceId: workspaceId,
          journeyId: journeyData.shortId,
          '*': 'attributes',
        }),
        {
          replace: true,
        },
      );

      // Allow the navigation to happen before launching
      requestAnimationFrame(() => {
        showAddAttributeSidebar({
          isTravellerAttribute: false,
          onCreate: (data: JourneyAttributeData) =>
            createAttributeMutation.mutateAsync({ journeyShortId: journeyData.shortId, data }),
        });
      });
    } else {
      showAddAttributeSidebar({
        isTravellerAttribute: false,
        onCreate: (data: JourneyAttributeData) =>
          createAttributeMutation.mutateAsync({ journeyShortId, data }),
      });
    }
  };

  return addAttribute;
};

export const CreateJourneyFieldHeaderButton = ({ journeyShortId }: { journeyShortId: string }) => {
  const addAttribute = useAddJourneyField(journeyShortId);

  return <CreateJourneyFieldButton onClick={addAttribute} />;
};

const CreateJourneyFieldButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Button
      iconLeft={<IconAddPlus />}
      variant="secondary"
      onClick={onClick}
      text={t('addAttribute')}
    />
  );
};

export const JourneyFields = ({
  attributes: journeyAttributes,
  journeyShortId,
  searchStr = '',
}: {
  attributes: AttributeType[];
  journeyShortId: string;
  searchStr?: string;
}) => {
  const [attributes, setAttributes] = useState<Array<AttributeType>>(journeyAttributes);
  const [searchParams, setSearchParams] = useSearchParams();
  // Captured once: an attribute to flash after jumping here from the review panel.
  const [highlightId] = useState(() => searchParams.get('highlight') ?? '');
  const { t } = useScopedTranslate(locale);
  const updateAttributeMutation = useUpdateAttribute(journeyShortId);
  const updateAttributeOrdersMutation = useUpdateAttributeOrders(journeyShortId);
  const removeAttributeMutation = useRemoveAttribute(journeyShortId);
  const addAttribute = useAddJourneyField(journeyShortId);

  useEffect(() => {
    setAttributes(journeyAttributes);
  }, [journeyAttributes]);

  useEffect(() => {
    if (searchParams.get('highlight')) {
      searchParams.delete('highlight');
      setSearchParams(searchParams, { replace: true });
    }
    // Run once on mount to strip the param after capturing it.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAttributeUpdate = (updatedAttribute: AttributeType) => {
    setAttributes((prevAttributes) =>
      prevAttributes.map((attr) =>
        attr.shortId === updatedAttribute.shortId ? updatedAttribute : attr,
      ),
    );
  };
  const onAttributeRemove = (shortId: string) => {
    setAttributes((prevAttributes) => prevAttributes.filter((attr) => attr.shortId !== shortId));
  };

  const filteredAttributes = useMemo(
    () =>
      attributes.filter((attribute) => {
        return attribute.title.toLowerCase().includes(searchStr.toLowerCase());
      }),
    [attributes, searchStr],
  );

  const changeAttributeOrder = (activeIndex: number, overIndex: number) => {
    // SortableList reports indices within the filtered list, not the full one.
    const activeShortId = filteredAttributes[activeIndex]?.shortId;
    const overShortId = filteredAttributes[overIndex]?.shortId;
    if (!activeShortId || !overShortId) return;
    const fullActive = attributes.findIndex((attr) => attr.shortId === activeShortId);
    const fullOver = attributes.findIndex((attr) => attr.shortId === overShortId);
    if (fullActive < 0 || fullOver < 0) return;

    const newAttributes = reorderList(attributes, fullActive, fullOver);
    setAttributes(newAttributes);
    updateAttributeOrdersMutation.mutate(
      newAttributes.reduce((acc, curr, idx) => ({ ...acc, [curr.shortId]: idx }), {}),
    );
  };

  const hasNoMatches = attributes.length > 0 && filteredAttributes.length === 0;

  return (
    <PageContent>
      <MiddleColumnPage>
        <Stack space={4}>
          <Stack space={2}>
            <Stack space={2} orientation="horizontal" align="center">
              <Text font="bold18" text={t('traveller')} />
              <Text font="bold18" color="textBullet" text="•" />
              <Text font="bold18" text={t('journeyFields')} />
            </Stack>
            <Text font="regular14" color="textSecondary" text={t('travellerSettingsDescription')} />
          </Stack>
          <Stack space={2}>
            {attributes.length === 0 && (
              <EmptyStateCell icon={<IconAttributeJourney />} text={t('noAttributes')} />
            )}
            {hasNoMatches && <EmptySearchState variant="small" skipMargin />}
            <SortableList<AttributeComponentProps, AttributeItem>
              onChange={changeAttributeOrder}
              items={filteredAttributes}
              idProperty="shortId"
              DraggableComponent={Attribute}
              componentProps={{
                highlightId,
                update: updateAttributeMutation,
                onAttributeUpdate,
                remove: removeAttributeMutation,
                onAttributeRemove,
              }}
            />
            <div>
              <CreateJourneyFieldButton onClick={addAttribute} />
            </div>
          </Stack>
        </Stack>
      </MiddleColumnPage>
    </PageContent>
  );
};

type AttributeItem = AttributeType;

type AttributeComponentProps = {
  highlightId: string;
  update: UseMutationResult<
    any,
    unknown,
    {
      shortId: string;
      data?: AttributeData;
    },
    unknown
  >;
  remove: UseMutationResult<any, unknown, string, unknown>;
  onAttributeUpdate: (updatedAttribute: AttributeType) => void;
  onAttributeRemove: (shortId: string) => void;
};

const stopPropagation = (event: MouseEvent) => {
  event.stopPropagation();
};

const Attribute = ({
  item: attribute,
  attributes,
  listeners,
  highlightId,
  update,
  remove,
  onAttributeUpdate,
  onAttributeRemove,
}: AttributeComponentProps & DraggableComponentProps<AttributeType>) => {
  const { t } = useScopedTranslate(locale);
  const showDeleteModal = useDeleteModal();
  const showAttributeSidebar = useFieldFormSidebar();
  const isActive = useIsSidebarActive(attribute.shortId);

  const showFn = () => {
    showAttributeSidebar(
      {
        isTravellerAttribute: false,
        onEdit: (data: JourneyAttributeData) => {
          return update.mutateAsync({
            shortId: attribute.shortId,
            data: {
              title: data.title,
              options: data.options,
              attributeType: data.attributeType,
              ...(data.attributeType === 'number' || data.attributeType === 'decimal'
                ? { isDecimal: data.isDecimal }
                : {}),
            },
          });
        },
        attribute,
      },
      { id: attribute.shortId },
    );
  };

  // The sidebar's click-outside listener fires on mousedown, before this
  // Card's onClick — so we register the continue target on mousedown too,
  // otherwise the unsaved-changes modal pops with a stale target.
  const registerContinue = () => {
    setContinueRegistry({ id: attribute.shortId, onContinue: showFn });
  };

  const openEditSidebar = () => {
    registerContinue();
    showFn();
  };

  const setAttributeActive = (isActive: boolean) => {
    onAttributeUpdate({ ...attribute, isActive });
    update.mutate({
      shortId: attribute.shortId,
      data: { isActive },
    });
  };

  return (
    <FocusBox isFocused={highlightId === attribute.shortId}>
      <FieldCard
        attributeType={attribute.attributeType}
        title={attribute.title}
        dragAttributes={attributes}
        dragListeners={listeners}
        isActive={isActive}
        onMouseDown={registerContinue}
        onClick={openEditSidebar}
        rightContent={
          <Stack orientation="horizontal" space={2} align="center" onClick={stopPropagation}>
            {!attribute.isActive && <Badge size="md" text={t('disabled')} />}
            <Menu
              buttonProps={{
                variant: 'ghost',
                size: 'sm',
                iconRight: <IconMoreVertical />,
              }}
            >
              <Menu.Item
                onSelect={openEditSidebar}
                label={t('editAttribute')}
                icon={<IconEdit />}
              />
              {attribute.isActive ? (
                <Menu.Item
                  onSelect={() => setAttributeActive(false)}
                  label={t('disable')}
                  icon={<IconVisibilityHidden />}
                />
              ) : (
                <Menu.Item
                  onSelect={() => setAttributeActive(true)}
                  label={t('enable')}
                  icon={<IconVisibilityShown />}
                />
              )}
              <Menu.Item
                onSelect={() => {
                  return showDeleteModal({
                    title: t('core.delete'),
                    description: t('areYouSureRemove'),
                    hasUserConfirmation: true,
                    buttonText: t('core.delete'),
                    buttonIcon: <IconTrash />,
                    onConfirm: () => {
                      onAttributeRemove(attribute.shortId);
                      remove.mutate(attribute.shortId);
                    },
                  });
                }}
                variant="danger"
                label={t('core.delete')}
                icon={<IconTrash />}
              />
            </Menu>
          </Stack>
        }
      />
    </FocusBox>
  );
};

const locale = {
  en: {
    traveller: 'Traveller',
    journeyFields: 'Journey fields',
    travellerSettingsDescription:
      'Add and configure the fields the traveller should have for this journey',
    noAttributes: 'No fields added',
    disabled: 'Disabled',
    disable: 'Disable',
    enable: 'Enable',
    addAttribute: 'Create Journey field',
    editAttribute: 'Edit',
    areYouSureRemove:
      "Are you sure you want to delete this field from the journey? This change will affect all travellers regardless of their journey state ('Not started', 'In progress', 'Completed').",
  },
};
